import {Timezones} from '@/utils/constants/timeZones'
import {format} from 'date-fns'
import {DateTime} from 'luxon'

const getJsDateFromObject = zoneDateTime => {
  const dateTime = zoneDateTime.toObject()

  if (!dateTime?.year || !dateTime?.month) return

  return new Date(dateTime.year, dateTime.month - 1, dateTime.day, dateTime.hour, dateTime.minute, dateTime.second)
}

/**
 * This function is used to format a UTC iso string to as human friendly text for specific country's timezone.
 */
export const formatPeriodDate = (date, country, formatStr = 'PPP') => {
  if (!date || !country) return ''

  const tz = Timezones[country]

  if (!tz) return undefined

  const zoneDateTime = DateTime.fromISO(date, {zone: Timezones.UTC}).setZone(tz)
  const dateAsObj = getJsDateFromObject(zoneDateTime)
  return dateAsObj ? format(dateAsObj, formatStr) : undefined
}

/*
 * This function is used to convert a UTC iso string to a local date object from a specific timezone.
 */
export const convertUtcStringToJsDate = ({date, timezone}) => {
  if (!date || !timezone) return

  const zoneDateTime = DateTime.fromISO(date, {zone: Timezones.UTC}).setZone(timezone)
  return getJsDateFromObject(zoneDateTime)
}

/**
 * This function is used to convert a local JS Date object to a Luxon DateTime object in a specific timezone.
 */
export const convertJsDateToDateTimeByTimezone = ({date, timezone}) => {
  if (!date) return

  const dateLocale = date.toLocaleDateString('en-US')
  return DateTime.fromFormat(dateLocale, 'M/d/yyyy', {zone: timezone})
}

export const isStartDateLessThanCurrentDate = startDate => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  if (startDate) {
    const [year, month, day] = startDate.split('-').map(Number) // Ensure correct parsing
    const parsedStartDate = new Date(year, month - 1, day) // Month is 0-based in JS
    if (parsedStartDate < today) {
      return true
    }
  }
  return false
}

/**
 * Combine a local date string (YYYY-MM-DD) and a time string (HH:mm or HH:mm:ss)
 * interpreted in the given timezone, and return a UTC ISO string.
 * Uses Luxon for timezone-aware conversion.
 */
export const combineLocalDateAndTimeToUtc = ({date, time, timezone = 'America/Mexico_City'}) => {
  if (!date || !time) return ''

  const [year, month, day] = date.split('-').map(Number)
  const timeParts = time.split(':').map(Number)
  const hour = timeParts[0] || 0
  const minute = timeParts[1] || 0
  const second = timeParts[2] || 0

  const dt = DateTime.fromObject({year, month, day, hour, minute, second}, {zone: timezone})

  return dt.toUTC().toISO({precision: 'second'})
}

export const splitUtcToLocalDateAndTime = (isoString, timezone = 'America/Mexico_City') => {
  if (!isoString) return '00:00'

  const dt = DateTime.fromISO(isoString, {zone: 'utc'}).setZone(timezone)

  return dt.toFormat('HH:mm')
}

export const buildTimestamp = (date, time) => {
  const [hours, minutes] = time.split(':').map(Number)
  const cleanMinutes = isNaN(minutes) ? 0 : minutes
  const d = new Date(date)
  d.setHours(hours, cleanMinutes, 0, 0)
  return d.getTime()
}
